import { useRef, useState } from "react";

/**
 * Toggle an `isDragging` flag while a file drag is hovering this element
 * or any descendant. Uses a counter (vs. a plain boolean) because
 * `dragenter`/`dragleave` fire on every child crossed — without
 * compensating for that, dragging onto a button inside the drop zone
 * would briefly mark the parent as "left."
 *
 * Spread `dragHandlers` onto the wrapper element you want to highlight.
 * Doesn't preventDefault — the ai-elements PromptInput's own form-level
 * drop handler does that, this hook is purely cosmetic.
 */
export function useDropHighlight() {
  const [isDragging, setIsDragging] = useState(false);
  const counterRef = useRef(0);

  const onDragEnter = (e: React.DragEvent) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    counterRef.current += 1;
    setIsDragging(true);
  };

  const onDragLeave = (e: React.DragEvent) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    counterRef.current -= 1;
    if (counterRef.current <= 0) {
      counterRef.current = 0;
      setIsDragging(false);
    }
  };

  const onDrop = () => {
    counterRef.current = 0;
    setIsDragging(false);
  };

  return { isDragging, dragHandlers: { onDragEnter, onDragLeave, onDrop } };
}
